import { defineComponent as _defineComponent } from 'vue'
import {
  computed, onMounted, PropType, ref, toRefs
} from 'vue';
import {
  AcceptOfferRequest, ProjectOffer, ProjectWithOffers, RejectOfferRequest
} from '@/models/MyProjects';
import BCXUserBanner from '@/components/molecules/BCXUserBanner.vue';
import MyProjectsOfferDetailActions from '@/views/my-projects/components/offer-details/MyProjectsOfferDetailActions.vue';
import MyProjectsOfferDetailConditions from '@/views/my-projects/components/offer-details/MyProjectsOfferDetailConditions.vue';
import BCXMarkdown from '@/components/molecules/BCXMarkdown.vue';
import BCXHelpTextTriggerIcon from '@/components/molecules/BCXHelpTextTriggerIcon.vue';
import BCXInfoCollapsible from '@/components/molecules/BCXInfoCollapsible.vue';
import MyProjectsOfferDetailAnswerBox from '@/views/my-projects/components/offer-details/MyProjectsOfferDetailAnswerBox.vue';
import { useMyProjects } from '@/views/my-projects/composables/useMyProjects';
import X from '@/components/svg-components/X.vue';
import ProjectListItem from '@/views/project-forum/components/ProjectListItem.vue';
import useUser from '@/mixins/useUser';
import useMessengerState from '@/state/messenger/messengerState';
import BCXConfirm from '@/components/molecules/BCXConfirm.vue';
import useBCXConfirm from '@/mixins/useBCXConfirm';
import {
  declineOffer, deleteProjectOffer, negotiateProjectOffer, postBlockOffersFromUser
} from '@/services/OffersService';
import { watchImmediate } from '@vueuse/core';
import convertProjectToProjectListItemProject from '@/utils/convertProjectToProjectListItemProject';
import ProjectListItemSkeleton from '@/views/project-forum/components/ProjectListItemSkeleton.vue';
import { BaseUserWithRole } from '@/models/User';
import usePageState from '@/state/pageState';
import useNotificationState from '@/state/notificationState';
import MyProjectsOfferDetailConditionsSkeleton from '@/views/my-projects/components/offer-details/MyProjectsOfferDetailConditionsSkeleton.vue';
import getAxiosResponseErrorCode from '@/utils/getAxiosResponseErrorCode';
import useFormattedDateTime from '@/mixins/useFormattedDateTime';
import useQuickInfo from '@/state/quickInfo';
import { createContractFromOffer } from '@/services/ContractService';
import useMeetingScheduler from '@/components/meeting-scheduler/composables/useMeetingScheduler';


export default /*#__PURE__*/_defineComponent({
  __name: 'MyProjectsOfferDetail',
  props: {
  offer: {
    type: Object as PropType<ProjectOffer>,
    required: true
  },
  project: {
    type: Object as PropType<ProjectWithOffers['project']>,
    required: true
  },
  type: {
    type: String as PropType<'made' | 'received'>,
    required: true
  }
},
  emits: ['close'],
  setup(__props, { emit }) {

const props = __props;



const { offer, project, type } = toRefs(props);

const fullOffer = ref<ProjectOffer | null>(null);
const fullProject = ref<ProjectWithOffers['project'] | null>(null);
const isCRUDLoading = ref(false);
const isDetailedLoading = ref(false);

const { showQuickInfo } = useQuickInfo();



const { globalErrorMessage } = usePageState();
const {
  isNarrow, loadProjectsWithOffers, loadOffer, isLoading: isSearchLoading, loadProject, markOfferAsRead, checkOfferForChanges
} = useMyProjects();
const { createNewChat, isOpened, loadChats } = useMessengerState();
const { loadNotifications } = useNotificationState();

const isHelpOpened = ref(false);
const isAnswering = ref(false);
const answerMode = ref('');

const isLoading = computed(() => isCRUDLoading.value || isSearchLoading.value || isDetailedLoading.value);

const { tUserFallback: t } = useUser('self');

const canPickOrReject = computed(() => !['DECLINED', 'WITHDRAWN', 'WITHDRAWN_QUEUED', 'ACCEPTED', 'AUTO_REJECTED', 'EXPIRED', 'CONTRACTPROCESS']
  .includes(fullOffer.value?.status ?? ''));

const isContractCreated = computed(() => fullOffer.value?.status === 'CONTRACTPROCESS');

const user = computed(() => (type.value === 'received' ? offer.value?.creator : fullProject.value?.creator as BaseUserWithRole));
const userId = computed(() => user.value?.userId ?? false);

const sendMessage = async (messageKey?: string) => {
  const otherUser = user.value;
  if (!otherUser) return;

  const preFill = messageKey && `${t(`my-projects.offers-detail.${messageKey}`, { title: project.value?.title ?? '?' })}`;
  await createNewChat(otherUser, preFill);

  if (offer.value.status === 'OPEN' && type.value === 'received') {
    const projectId = project.value?.id;
    const offerId = offer.value?.id;

    await negotiateProjectOffer(projectId, offerId);

    await loadProjectsWithOffers({
      invalidateProjectId: projectId,
      invalidateOfferId: offerId,
      invalidateAll: false
    });
  }

  isOpened.value = true;
};

const confirmText = ref('');

const { showConfirm, ask, onConfirm } = useBCXConfirm();
const { openCreate } = useMeetingScheduler();

const offerCRUD = async (action: 'withdraw' | 'reject' | 'accept', requestData?:AcceptOfferRequest | RejectOfferRequest, doConfirm: boolean): Promise<boolean> => {
  const projectId = project.value?.id;
  const offerId = offer.value?.id;
  if (!projectId || !offerId || isCRUDLoading.value) return false;

  let confirmed = true;

  if (doConfirm) {
    confirmText.value = `${t(`my-projects.offers-detail.confirms_USERTYPE.${action}`)}`;
    confirmed = await ask();
  }

  if (confirmed) {
    isCRUDLoading.value = true;
    try {
      switch (action) {
        case 'withdraw':
          await deleteProjectOffer(projectId, offerId);
          break;
        case 'reject':
          await declineOffer(projectId, offerId, requestData as RejectOfferRequest);
          break;
        case 'accept':
          await createContractFromOffer(projectId, offerId, requestData as AcceptOfferRequest);
          break;
      }
    } catch (err: AxiosError) {
      globalErrorMessage.value = {
        i18Key: 'my-projects.errors.crud-error',
        code: getAxiosResponseErrorCode(err),
        component: 'MyProjectsOfferDetail'
      };
      return false;
    } finally {
      isCRUDLoading.value = false;
    }

    isAnswering.value = false;
    await loadProjectsWithOffers({
      invalidateProjectId: projectId,
      invalidateOfferId: offerId,
      invalidateAll: false
    });
    setTimeout(() => {
      loadChats();
    }, 4000);
    return true;
  }
  return false;
};

const blockOffersFromUser = () => {
  const userId = offer.value.creator?.userId ?? '';
  postBlockOffersFromUser(userId);
};

const withdrawOffer = async () => {
  await offerCRUD('withdraw', null, true);
};

const rejectOffer = async (rejectData: RejectOfferRequest) => {
  await offerCRUD('reject', rejectData, false);
};

const acceptOffer = async (acceptData: AcceptOfferRequest) => {
  await offerCRUD('accept', acceptData, false);
};

const showQuickProfile = (target: HTMLElement) => {
  showQuickInfo(target, user.value, 'show-more');
};

const scheduleMeeting = () => {
  if (user.value?.userId) openCreate(user.value.userId);
};

watchImmediate([offer, project, isSearchLoading], async ([offer, project, isSearchLoading]) => {
  if (isSearchLoading) return;
  const projectId = (project as ProjectWithOffers['project'])?.id;
  if (!projectId || !offer) return;

  isDetailedLoading.value = true;
  const loadedOffer = await loadOffer(projectId, (offer as ProjectWithOffers['project']).id);

  if (loadedOffer) fullOffer.value = loadedOffer;
  if (loadedOffer?.read) await loadNotifications();

  const loadedProject = await loadProject(projectId);
  if (loadedProject) fullProject.value = loadedProject;
  isDetailedLoading.value = false;
});

const projectListItem = computed(() => fullProject.value && convertProjectToProjectListItemProject(fullProject.value));

const formatDate = (timestamp: string, dateformat: 'short'|'diff' = 'diff') => {
  const { getFormattedTimeByAge, getFormattedDate } = useFormattedDateTime();
  if (dateformat === 'diff') return getFormattedTimeByAge(timestamp);
  return getFormattedDate(timestamp);
};

const creationDateFormatted = () => formatDate(fullOffer.value?.created ?? '');

const updatedDateFormatted = () => formatDate(fullOffer.value?.lastUpdated ?? '');

const tUser = (key: string, ...params:any) => {
  const { tUserFallback } = useUser('self');
  return tUserFallback(key, ...params);
};

const bgImage = computed(() => {
  const status = offer.value?.status;
  if (status === 'ACCEPTED') {
    return 'accepted';
  } if (status === 'DECLINED' || status === 'WITHDRAWN' || status === 'WITHDRAWN_QUEUED' || status === 'EXPIRED') {
    return 'declined';
  }
  return 'none';
});

const extendValidity = ref(false);
const extendValidityOn = () => {
  extendValidity.value = true;
};
const extendValidityAbort = () => {
  extendValidity.value = false;
};
const updatedOffer = async (offer: ProjectOffer) => {
  fullOffer.value = offer;
  extendValidity.value = false;
};

onMounted(async () => {
  markOfferAsRead(offer.value.id);
  if (!isSearchLoading.value) {
    await checkOfferForChanges(project.value.id, offer.value.id);
  }
});

const isOfferOpen = computed(() => offer.value?.status === 'OPEN' || offer.value?.status === 'OPEN_CHANGED');

return { __sfc: true,props, offer, project, type, fullOffer, fullProject, isCRUDLoading, isDetailedLoading, showQuickInfo, emit, globalErrorMessage, isNarrow, loadProjectsWithOffers, loadOffer, isSearchLoading, loadProject, markOfferAsRead, checkOfferForChanges, createNewChat, isOpened, loadChats, loadNotifications, isHelpOpened, isAnswering, answerMode, isLoading, t, canPickOrReject, isContractCreated, user, userId, sendMessage, confirmText, showConfirm, ask, onConfirm, openCreate, offerCRUD, blockOffersFromUser, withdrawOffer, rejectOffer, acceptOffer, showQuickProfile, scheduleMeeting, projectListItem, formatDate, creationDateFormatted, updatedDateFormatted, tUser, bgImage, extendValidity, extendValidityOn, extendValidityAbort, updatedOffer, isOfferOpen, BCXUserBanner, MyProjectsOfferDetailActions, MyProjectsOfferDetailConditions, BCXMarkdown, BCXHelpTextTriggerIcon, BCXInfoCollapsible, MyProjectsOfferDetailAnswerBox, X, ProjectListItem, BCXConfirm, ProjectListItemSkeleton, MyProjectsOfferDetailConditionsSkeleton }
}

})